import { apiClient } from './client'
import type { ApiResponse } from './client'
import type { LookupOptionDto, TimeZoneDto, EmailRuleLookupResponse, ReplyToAddressLookupDto, SendingDomainOptionDto, EmailTypeOption, VariableCatalogResponse } from '@/types/lookups'

export const lookupsApi = {
  // TouchPoint types
  getTouchPointTypes: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/TouchPointTypes', { headers })
  },

  // Meeting types
  getCampaignMeetingTypes: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/CampaignMeetingTypes', { headers })
  },

  getWebMeetingTypes: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/WebMeetingTypes', { headers })
  },

  // Web statuses
  getWebStatuses: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/WebStatuses', { headers })
  },

  // Company size ranges
  getCompanySizeRanges: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/CompanySizeRanges', { headers })
  },

  // Time zones (uses specialized DTO with additional fields)
  getTimeZones: (ifNoneMatch?: string): Promise<ApiResponse<TimeZoneDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/TimeZones', { headers })
  },

  // UTM sources
  getUtmSources: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/UtmSources', { headers })
  },

  // Social media platforms
  getPlatforms: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/Platforms', { headers })
  },

  // Web types
  getWebTypes: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/WebTypes', { headers })
  },

  // Authentication types
  getAuthenticationTypes: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/AuthenticationTypes', { headers })
  },

  // Campaign statuses
  getCampaignStatuses: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/CampaignStatuses', { headers })
  },

  // Company statuses
  getCompanyStatuses: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/CompanyStatuses', { headers })
  },

  // File statuses
  getFileStatuses: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/FileStatuses', { headers })
  },

  // Online event types
  getOnlineEventTypes: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/OnlineEventTypes', { headers })
  },

  // Online mode types
  getOnlineModeTypes: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/OnlineModeTypes', { headers })
  },

  // Speaker types
  getSpeakerTypes: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/SpeakerTypes', { headers })
  },

  // Workspace statuses
  getWorkspaceStatuses: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/WorkspaceStatuses', { headers })
  },

  // Countries
  getCountries: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/Countries', { headers })
  },

  // Asset types
  getAssetTypes: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/AssetTypes', { headers })
  },

  // Asset statuses
  getAssetStatuses: (ifNoneMatch?: string): Promise<ApiResponse<LookupOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get('/api/App/Lookups/AssetStatuses', { headers })
  },

  // Email rules — legal anchors, excluded reasons, and per-EmailTypeKey defaults for a given Web
  getEmailRules: (workspaceId: string, webId: string, ifNoneMatch?: string): Promise<ApiResponse<EmailRuleLookupResponse>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get(`/api/Workspaces/${workspaceId}/Webs/${webId}/Lookups/EmailRules`, { headers })
  },

  // ORB-363: the web's email-type catalog for the ad-hoc "create email copy" picker. Empty for web
  // types without a catalog (Meeting/Demo).
  getEmailTypes: (workspaceId: string, webId: string): Promise<ApiResponse<EmailTypeOption[]>> => {
    return apiClient.get(`/api/Workspaces/${workspaceId}/Webs/${webId}/Lookups/EmailTypes`)
  },

  // ORB-466: the web's template-variable catalog — one vocabulary shared by landing pages and
  // emails, with each variable's live resolved value for THIS web. Backs the token reference
  // panel in both builders and seeds Unlayer's merge-tag menu. Note this endpoint hangs off the
  // Web directly, not under /Lookups, per the ORB-466 contract.
  getVariableCatalog: (workspaceId: string, webId: string): Promise<ApiResponse<VariableCatalogResponse>> => {
    return apiClient.get(`/api/Workspaces/${workspaceId}/Webs/${webId}/VariableCatalog`)
  },

  // Verified sending domains for the workspace's company — drives the "Send from" selector
  // in the email activation editor.
  getVerifiedSendingDomains: (workspaceId: string, ifNoneMatch?: string): Promise<ApiResponse<SendingDomainOptionDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get(`/api/Workspaces/${workspaceId}/Lookups/VerifiedSendingDomains`, { headers })
  },

  // Company reply-to addresses for the workspace's company — drives the "Reply-To" selector
  // in the email activation editor. Each option is { Value, DisplayName, IsDefault }.
  getReplyToAddresses: (workspaceId: string, ifNoneMatch?: string): Promise<ApiResponse<ReplyToAddressLookupDto[]>> => {
    const headers: Record<string, string> = {}
    if (ifNoneMatch) {
      headers['If-None-Match'] = ifNoneMatch
    }
    return apiClient.get(`/api/Workspaces/${workspaceId}/Lookups/ReplyToAddresses`, { headers })
  }
}
